import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Message, ReplyChannel, ReplyResult } from "@school-sales-ai/shared";
import { api } from "@/lib/api";

/**
 * Send a reply from the CRM, on the chat the advisor picked (WhatsApp or Instagram).
 *
 * Optimistic, following `useMoveCard`: the advisor's own words must appear the
 * instant they hit send, or the box feels broken and they send twice. The
 * placeholder is replaced when the poll or the invalidate brings the real row.
 *
 * On failure the placeholder is rolled back — the composer keeps the text, so
 * nothing is lost, and the timeline never shows a message the prospect did not
 * receive.
 */
export function useSendReply(leadId: string) {
  const qc = useQueryClient();
  const messagesKey = ["messages", leadId] as const;

  return useMutation({
    mutationFn: ({ content, channel }: { content: string; channel: ReplyChannel }) =>
      api.post<ReplyResult>(`/crm/leads/${leadId}/reply`, { content, channel }),

    onMutate: async ({ content, channel }: { content: string; channel: ReplyChannel }) => {
      await qc.cancelQueries({ queryKey: messagesKey });
      const snapshot = qc.getQueryData<{ messages: Message[] }>(messagesKey);

      const optimistic: Message = {
        // Temporary id and seq: replaced by the server's row on settle. Negative
        // seq keeps it sorted last without colliding with the real sequence.
        id: `optimistic-${Date.now()}`,
        leadId,
        channel,
        role: "human_agent",
        content,
        seq: Number.MAX_SAFE_INTEGER,
        status: "complete",
        messageType: "text",
        // The real row will carry the author; the placeholder does not pretend
        // to know it, so the bubble reads "Agente" for the instant it exists.
        userId: null,
        authorName: null,
        isReengagement: false,
        createdAt: new Date().toISOString(),
      };
      qc.setQueryData<{ messages: Message[] }>(messagesKey, (old) =>
        old ? { ...old, messages: [...old.messages, optimistic] } : old,
      );

      return { snapshot };
    },

    onSuccess: (result) => {
      // The message reached the prospect either way — this is the case where we
      // could not write it to the timeline. Saying so is what stops the advisor
      // from retyping and sending it twice; the row simply will not appear, and
      // there is no way to recover it (neither transport echoes our own sends).
      if (!result.recorded) {
        toast.warning(
          "Se envió, pero no se pudo registrar en el historial. No lo reenvíes.",
        );
      }
    },

    onError: (err: Error, _content, context) => {
      if (context?.snapshot) qc.setQueryData(messagesKey, context.snapshot);
      toast.error(err.message || "No se pudo enviar el mensaje");
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: messagesKey });
      // The reply pauses the AI and moves the lead's activity, so the detail and
      // the Buzón both go stale the moment it lands.
      void qc.invalidateQueries({ queryKey: ["lead", leadId] });
      void qc.invalidateQueries({ queryKey: ["inbox"] });
      void qc.invalidateQueries({ queryKey: ["inbox-unread"] });
    },
  });
}
